<template>
  <div class="w-full px-6 py-4">
    <h5 class="text-gray-800 text-sm font-medium mb-1 leading-none dark:text-gray-100">
      {{ $t('roster.normalHours') }}
    </h5>
    <p class="mb-12 text-sm text-gray-600 dark:text-gray-300">
      {{ $t('components.resource.edit.roster.info') }}
    </p>
    <div class="mb-6">
      <o-field-group :label="$t('general.frequency')" :info="$t('roster.rosterInterval')" col class="mb-3 block">
        <segment-field v-model="frequency" :options="frequencyOptions" />
      </o-field-group>
    </div>

    <div class="mt-12">
      <div class="mb-3 flex items-center justify-between">
        <div v-if="week">
          <div class="flex items-center space-x-2 mb-1">
            <template v-if="!isRosterFrequencyWeekly">
              <p class="opacity-90 font-medium">
                {{ $t('roster.week') }}
              </p>
              <o-chip size="xs" variant="success" flat>
                {{ week.weekLabel }}
              </o-chip>
            </template>

            <p v-else class="opacity-90 font-medium">
              {{ $t('general.roster') }}
            </p>
          </div>
          <p class="opacity-80 text-sm">
            {{ $t('roster.starting', { date: week.dateLabel }) }}
          </p>
        </div>

        <div>
          <div v-if="frequency && !isRosterFrequencyWeekly">
            <segment-field v-model="week" :options="weekOptions" />
          </div>
        </div>
      </div>
      <o-table
        v-if="week"
        class="w-full"
        container-class="border rounded"
        :data="roster.weeks[week.week - 1].days"
        :headers="[
          {text: $t('datetime.labels.day')},
          {text: $t('general.label')},
          {text: $t('roster.working')},
          {text: $t('general.startTime'), className: 'w-32'},
          {text: $t('roster.finishTime'), className: 'w-32'},
          {text: $t('roster.breaks')}
        ]"
        :limit="20"
        :tbody-class="{
          tfoot: '',
          tr: 'transition-all duration-200 bg-gray-50 dark:bg-gray-900',
          td: 'py-3 px-4 text-gray-800 text-sm dark:text-gray-100',
          stripe: 'bg-gray-50/50 dark:bg-gray-900'
        }"
        :thead-class="{
          thead: 'border-b',
          tr: 'border-blue-700',
          th: 'font-normal p-4 pb-3 bg-gray-50 text-gray-700 text-sm sticky top-0 dark:bg-gray-900 dark:text-gray-200',
          content: 'flex item-center'
        }"
        striped
        collapse
        scroll
      >
        <template #row="props">
          <td :class="props.tdClass">
            {{ props.row.dayLabel }}
          </td>
          <td :class="props.tdClass">
            <text-field v-model="props.row.hour.label" />
          </td>
          <td :class="props.tdClass">
            <checkbox-field v-model="props.row.hour.working" option-class="p-1 items-center rounded transition duration-200" />
          </td>
          <td :class="props.tdClass">
            <o-field-group v-slot="{ status }" :name="$t('general.startTime')" :label="$t('general.startTime')">
              <time-field v-model="props.row.hour.startTime" :augmenter="Augmenters.minutes" :status="status" />
            </o-field-group>
          </td>
          <td :class="props.tdClass">
            <o-field-group v-slot="{ status }" :name="$t('roster.finishTime')" :label="$t('roster.finishTime')">
              <time-field v-model="props.row.hour.finishTime" :augmenter="Augmenters.minutes" :status="status" />
            </o-field-group>
          </td>
          <td :class="props.tdClass">
            <div clas="flex items-center">
              <p class="inline mr-2 text-sm text-gray-800 dark:text-gray-100">
                {{ props.row.breaks.length }}
              </p>
              <o-button flat size="xs" @click="props.toggle">
                {{ $t('general.edit') }}
              </o-button>
            </div>
          </td>
        </template>

        <template #collapse="{ row }">
          <breaks v-model:breaks="row.breaks" :day="row" />
        </template>
      </o-table>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'

import Breaks from '@/components/roster/elements/edit/RosterEditBreaks.vue'

import {
  OTable,
} from '@/components/elements'

import {
  SegmentField,
  TimeField,
  CheckboxField,
  TextField,
} from '@/components/fields'

import { augmenters as Augmenters } from '@/composables/useFieldAugmenters'

interface Props {
  resource: Record<string, any>
  roster: Record<string, any>
}

const props = defineProps<Props>()

const emit = defineEmits<{
  (e: 'update:roster', value: Record<string, any>): void
}>()

const frequency = ref<any>(null)
const week = ref<any>(null)

const _roster = computed({
  get () {
    return props.roster
  },
  set (value) {
    emit('update:roster', value)
  },
})

const frequencyOptions = computed(() => {
  const roster = props.roster
  const frequencies = roster.frequencies

  return frequencies
    .map((item: any) => {
      return {
        label: item.label,
        value: item,
      }
    })
})

const weekOptions = computed(() => {
  const freq = frequency.value
  const weeks = freq.currentWeeks

  return weeks
    .sort((a: any, b: any) => a.week - b.week)
    .map((item: any) => {
      return {
        label: item.weekLabel,
        value: item,
      }
    })
})

const isRosterFrequencyWeekly = computed(() => {
  return props.roster.isRosterFrequencyWeekly
})

const isRosterFrequencyFortnightly = computed(() => {
  return props.roster.isRosterFrequencyFortnightly
})

const isRosterFrequencyMonthly = computed(() => {
  return props.roster.isRosterFrequencyMonthly
})

watch(() => props.roster, (roster) => {
  if (roster) {
    const freq = roster.frequency
    const frequencies = roster.frequencies

    const match = frequencies.find((item: any) => item.frequency === freq)

    frequency.value = match
  }
}, { immediate: true })

watch(frequency, (value) => {
  if (value) {
    const freq = value.frequency
    const currentWeeks = value.currentWeeks

    _roster.value.frequency = freq
    week.value = currentWeeks[0]
  }
}, { immediate: true })
</script>
